/**
 * MARK:- Buy Box Accessory Web Component
 * Styled buy box for accessory products with quantity dropdown,
 * save badge, stock indicator, and enhanced add-to-cart button.
 */
class BuyBoxAccessory extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.initializeData();
    this.bindElements();
    this.bindEvents();

    // Set initial quantity to 1
    const quantitySelect = this.querySelector('[data-accessory-element="quantity-select"]');
    if (quantitySelect) {
      quantitySelect.value = "1";
    }

    // Trigger initial display update
    this.handleQuantityChange();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.boundVariantChangeHandler) {
      document.removeEventListener("product:variant-change", this.boundVariantChangeHandler);
    }
  }

  initializeData() {
    const dataScript = this.querySelector("[data-accessory-variants]");
    if (dataScript) {
      try {
        const data = JSON.parse(dataScript.textContent);
        this.variantData = data.variants;
        // MARK:- Fallback max qty from the default_max_purchase_qty cart setting
        this.defaultMaxQty = data.defaultMaxQty || 99;
      } catch (e) {
        console.error("BuyBoxAccessory: Failed to parse variant data", e);
      }
    }

    this.sectionId = this.dataset.sectionId;

    // Get initial variant from form
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      this.currentVariantId = variantInput.value;
    }
  }

  bindElements() {
    this.priceDisplay = this.querySelector('[data-accessory-element="price-display"]');
    this.addButton = this.querySelector('[data-accessory-element="add-button"]');
    this.saveBadgeWrapper = this.querySelector('[data-accessory-element="save-badge-wrapper"]');
    this.saveBadge = this.querySelector('[data-accessory-element="save-badge"]');
    this.stockIndicator = this.querySelector('[data-accessory-element="stock-indicator"]');

    // Value elements
    this.currentPriceValue = this.querySelector('[data-accessory-value="current-price"]');
    this.comparePriceValue = this.querySelector('[data-accessory-value="compare-price"]');
    this.buttonPriceValue = this.querySelector('[data-accessory-value="button-price"]');
  }

  refreshElements() {
    this.priceDisplay = this.querySelector('[data-accessory-element="price-display"]');
    this.addButton = this.querySelector('[data-accessory-element="add-button"]');
    this.saveBadgeWrapper = this.querySelector('[data-accessory-element="save-badge-wrapper"]');
    this.saveBadge = this.querySelector('[data-accessory-element="save-badge"]');
    this.stockIndicator = this.querySelector('[data-accessory-element="stock-indicator"]');
    this.currentPriceValue = this.querySelector('[data-accessory-value="current-price"]');
    this.comparePriceValue = this.querySelector('[data-accessory-value="compare-price"]');
    this.buttonPriceValue = this.querySelector('[data-accessory-value="button-price"]');
  }

  bindEvents() {
    // Event delegation for quantity select
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-accessory-element="quantity-select"]')) {
        this.handleQuantityChange();
      }
    });

    // Subscribe to variant changes via pubsub
    if (typeof subscribe !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.productVariantChange,
        this.handleVariantChange.bind(this)
      );
    }

    // Fallback: listen to custom event
    this.boundVariantChangeHandler = this.handleVariantChangeEvent.bind(this);
    document.addEventListener("product:variant-change", this.boundVariantChangeHandler);
  }

  handleQuantityChange() {
    const quantitySelect = this.querySelector('[data-accessory-element="quantity-select"]');
    const quantity = parseInt(quantitySelect?.value || "1", 10);

    const variantInfo = this.variantData?.[this.currentVariantId];
    if (!variantInfo) return;

    const unitPrice = variantInfo.price || 0;
    const compareAtPrice = variantInfo.compareAtPrice || 0;
    const totalPrice = unitPrice * quantity;

    // MARK:- MAP variants never show compare/save UI (mirrors onVariantChange mapActive handling)
    const mapActive = variantInfo.mapActive === true;
    const trulyAvailable = variantInfo.available === true &&
      variantInfo.isDisabled !== true &&
      variantInfo.isUpsellVariant !== true;

    // Calculate savings
    let hasSavings = false;
    let savingsPercent = 0;
    let comparePrice = 0;

    if (compareAtPrice > unitPrice && !mapActive && trulyAvailable) {
      hasSavings = true;
      const unitSavings = compareAtPrice - unitPrice;
      comparePrice = compareAtPrice * quantity;
      savingsPercent = Math.round((unitSavings / compareAtPrice) * 100);
    }

    // Update price display
    if (this.currentPriceValue) {
      this.currentPriceValue.textContent = this.formatMoney(totalPrice);
    }

    // MARK:- Keep every price value hidden for unavailable accessory variants
    if (this.priceDisplay) {
      this.priceDisplay.hidden = mapActive || !trulyAvailable;
    }

    if (this.comparePriceValue) {
      if (hasSavings) {
        this.comparePriceValue.textContent = this.formatMoney(comparePrice);
        this.comparePriceValue.hidden = false;
      } else {
        this.comparePriceValue.hidden = true;
      }
    }

    // Update save badge
    if (this.saveBadgeWrapper) {
      if (hasSavings) {
        this.saveBadgeWrapper.hidden = false;
        if (this.saveBadge) {
          // MARK:- Save % text format resolved per brand via data-save-format
          const saveFormat = this.saveBadge.dataset.saveFormat || "Save [percent]%";
          this.saveBadge.textContent = saveFormat.replace("[percent]", savingsPercent);
        }
      } else {
        this.saveBadgeWrapper.hidden = true;
      }
    }

    // Update button price
    if (this.buttonPriceValue) {
      this.buttonPriceValue.textContent = this.formatMoney(totalPrice);
      this.buttonPriceValue.hidden = mapActive || !trulyAvailable;
      const buttonSeparator = this.addButton?.querySelector('.buy-box-accessory__button-separator');
      if (buttonSeparator) buttonSeparator.hidden = mapActive || !trulyAvailable;
    }
  }

  handleVariantChangeEvent(event) {
    if (event.detail && event.detail.variant) {
      this.onVariantChange(event.detail.variant);
    }
  }

  handleVariantChange(data) {
    // Check if this event is for our section
    if (data.sectionId && data.sectionId !== this.sectionId) {
      return;
    }

    if (data.variant) {
      this.onVariantChange(data.variant);
    }
  }

  onVariantChange(variant) {
    if (!variant) return;

    this.currentVariantId = String(variant.id);

    // Refresh DOM references after Section Rendering API replacement
    this.refreshElements();

    const variantInfo = this.variantData?.[this.currentVariantId];

    // MARK:- Check Shopify availability, is_disabled, and is_upsell_variant metafields
    // Upsell variants are post-checkout-only (Aftersell) and must never be purchasable from the PDP.
    const isDisabled = variantInfo?.isDisabled === true;
    const isUpsellVariant = variantInfo?.isUpsellVariant === true;
    const trulyAvailable = variant.available && !isDisabled && !isUpsellVariant;

    // MARK:- Check MAP pricing status
    const mapActive = variantInfo?.mapActive === true;

    // Update variant input in form
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !trulyAvailable;
    }

    // MARK:- Update limit badge on variant change (respects data-limit-format template with [qty] placeholder)
    const limitBadge = this.querySelector('[data-accessory-element="limit-badge"]');
    if (limitBadge) {
      // Only show badge when maxQty metafield is explicitly set (not null)
      if (variantInfo?.maxQty != null) {
        limitBadge.hidden = false;
        limitBadge.dataset.limit = variantInfo.maxQty;
        const limitSpan = limitBadge.querySelector('span');
        if (limitSpan) {
          const format = limitBadge.dataset.limitFormat || 'Limit [qty] per customer';
          limitSpan.textContent = format.replace('[qty]', variantInfo.maxQty);
        }
      } else {
        limitBadge.hidden = true;
      }
    }

    // MARK:- Update MAP pricing display on variant change
    const mapDisplay = this.querySelector('[data-accessory-element="map-price-display"]');
    const priceDisplay = this.querySelector('[data-accessory-element="price-display"]');
    const buttonSeparator = this.addButton?.querySelector('.buy-box-accessory__button-separator');
    const buttonPrice = this.addButton?.querySelector('.buy-box-accessory__button-price');

    if (!trulyAvailable) {
      if (mapDisplay) mapDisplay.hidden = true;
      if (priceDisplay) priceDisplay.hidden = true;
      if (this.saveBadgeWrapper) this.saveBadgeWrapper.hidden = true;
      if (buttonSeparator) buttonSeparator.hidden = true;
      if (buttonPrice) buttonPrice.hidden = true;
    } else if (mapActive) {
      if (mapDisplay) mapDisplay.hidden = false;
      if (priceDisplay) priceDisplay.hidden = true;
      if (this.saveBadgeWrapper) this.saveBadgeWrapper.hidden = true;
      if (buttonSeparator) buttonSeparator.hidden = true;
      if (buttonPrice) buttonPrice.hidden = true;

      // Update MAP MSRP if present
      const mapMsrp = this.querySelector('[data-accessory-value="map-msrp"]');
      const mapMsrpWrapper = mapMsrp?.closest('.buy-box-accessory__map-msrp');
      if (mapMsrp && variantInfo?.compareAtPrice > 0) {
        mapMsrp.textContent = this.formatMoney(variantInfo.compareAtPrice);
        if (mapMsrpWrapper) mapMsrpWrapper.hidden = false;
      } else if (mapMsrpWrapper) {
        mapMsrpWrapper.hidden = true;
      }
    } else {
      if (mapDisplay) mapDisplay.hidden = true;
      if (priceDisplay) priceDisplay.hidden = false;
      if (buttonSeparator) buttonSeparator.hidden = false;
      if (buttonPrice) buttonPrice.hidden = false;
    }

    // Update button state
    if (this.addButton) {
      this.addButton.disabled = !trulyAvailable;

      const buttonText = this.addButton.querySelector(".buy-box-accessory__button-text");
      if (buttonText) {
        if (isDisabled) {
          buttonText.textContent = "Unavailable";
        } else if (!variant.available) {
          buttonText.textContent =
            window.variantStrings?.outOfStock ||
            window.themeStrings?.outOfStock ||
            "Out of stock";
        } else if (mapActive) {
          buttonText.textContent = this.addButton.dataset.mapText || "ADD TO CART FOR PRICE";
        } else if (variant.inventory_policy === "continue" && variant.inventory_quantity <= 0) {
          buttonText.textContent = window.variantStrings?.backorder || "Backorder";
        } else {
          buttonText.textContent =
            window.variantStrings?.addToCart || window.themeStrings?.addToCart || "Add To Cart";
        }
      }
    }

    // Update stock indicator
    if (this.stockIndicator) {
      const stockText = this.stockIndicator.querySelector(".buy-box-accessory__stock-text");
      const boltIcon = this.stockIndicator.querySelector("svg");

      if (stockText) {
        if (trulyAvailable) {
          stockText.textContent = "In Stock";
          stockText.className = "buy-box-accessory__stock-text buy-box-accessory__stock-text--in";
          if (boltIcon) boltIcon.style.display = "";
        } else {
          stockText.textContent = isDisabled ? "Unavailable" : "Out of Stock";
          stockText.className = "buy-box-accessory__stock-text buy-box-accessory__stock-text--out";
          if (boltIcon) boltIcon.style.display = "none";
        }
      }
    }

    // Rebuild quantity options and update display
    this.rebuildQuantityOptions();
    this.handleQuantityChange();
  }

  rebuildQuantityOptions() {
    const quantitySelect = this.querySelector('[data-accessory-element="quantity-select"]');
    if (!quantitySelect || !this.variantData || !this.currentVariantId) return;

    const variantInfo = this.variantData[this.currentVariantId];
    if (!variantInfo) return;

    // MARK:- Use null-coalescing for metafield values (null = no limit set)
    let minQty = variantInfo.minQty ?? 1;
    let maxQty = variantInfo.maxQty ?? (this.defaultMaxQty ?? 99);

    // Ensure valid range
    if (minQty < 1) minQty = 1;
    if (maxQty < minQty) maxQty = minQty;

    // Clear and rebuild options
    quantitySelect.innerHTML = "";

    for (let i = minQty; i <= maxQty; i++) {
      const option = document.createElement("option");
      option.value = i;
      option.textContent = i;
      quantitySelect.appendChild(option);
    }

    // Reset to minQty on variant change
    quantitySelect.value = minQty;
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || "${{amount}}");
    }

    // Fallback formatting with proper comma separators
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const amountNoDecimals = Math.floor(cents / 100).toLocaleString('en-US');
    const moneyFormat = window.theme?.moneyFormat || "${{amount}}";

    return moneyFormat
      .replace("{{amount}}", amount)
      .replace("{{amount_no_decimals}}", amountNoDecimals)
      .replace("{{amount_with_comma_separator}}", amount.replace(",", ".").replace(".", ","))
      .replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimals.replace(",", "."))
      .replace("{{amount_with_apostrophe_separator}}", amount.replace(",", "'"));
  }
}

// Register the custom element
if (!customElements.get("buy-box-accessory")) {
  customElements.define("buy-box-accessory", BuyBoxAccessory);
}
